import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { EditButton, Checkbox } from "../../Components/Buttons";
import { entityIdKeyMap } from "../../EntityKeys";

export interface FieldDefinition {
  name: string;
  label: string;
  type: string;
  options?: readonly { value: string | number; label: string }[];
  required?: boolean;
}

interface UseTableColumnsProps {
  data: any[];
  category: string;
  fields?: readonly FieldDefinition[];
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  handleRowClick?: (item: any) => void;
}

export const useTableColumns = ({
  data,
  category,
  fields,
  selectedIds,
  setSelectedIds,
  handleRowClick,
}: UseTableColumnsProps) => {
  const columns = useMemo<ColumnDef<any>[]>(() => {
    if (!data || data.length === 0) return [];

const cols: ColumnDef<any>[] = [
      // 1. Checkbox Column
      {
        id: "select",
        header: () => null,
        cell: ({ row }) => {
          const item = row.original;
          const idKey =
            entityIdKeyMap[category as keyof typeof entityIdKeyMap] || "id";
          const rowId = item[idKey] || item.id || item._id || row.index;
          const isSelected = selectedIds.includes(rowId);

          return (
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedIds((prev) =>
                  prev.includes(rowId)
                    ? prev.filter((id) => id !== rowId)
                    : [...prev, rowId]
                );
              }}
            />
          );
        },
      },
    ];

    // 2. Dynamic Data Columns με βάση τα Labels
    if (fields && fields.length > 0) {
      fields.forEach((field) => {
        cols.push({
          accessorKey: field.name,
          header: field.label,
          cell: ({ getValue }) => {
            const val = getValue();
            if (field.type === "select" && field.options) {
              const matchedOption = field.options.find(
                (opt) => String(opt.value) === String(val)
              );
              return matchedOption ? matchedOption.label : (val !== undefined && val !== null ? String(val) : "-");
            }

            return val !== undefined && val !== null ? String(val) : "-";
          },
        });
      });
    }
     else {
      // Fallback: Αν δεν δοθούν fields, απομονώνει αυτόματα τα κλειδιά από το data[0]
      const dynamicKeys = Object.keys(data[0])
        .filter((key) => !key.toLowerCase().includes("id") && key !== "__v")
        .slice(0, 4);

      dynamicKeys.forEach((key) => {
        cols.push({
          accessorKey: key,
          header: key,
          cell: ({ getValue }) => {
            const val = getValue();
            return val !== undefined && val !== null ? String(val) : "-";
          },
        });
      });
    }

    // 3. Actions Column (Edit Button)
    if (handleRowClick) {
      cols.push({
        id: "actions",
        header: () => <div className="text-right">Ενέργειες</div>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <EditButton
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row.original);
              }}
            />
          </div>
        ),
      });
    }

    return cols;
  }, [data, category, fields, selectedIds, setSelectedIds, handleRowClick]);

  return columns;
};